import { Text } from './Text';
import { StepStatus } from '../../types/ui/stepStatus';
import { ConnectorStatus } from '../../types/ui/connectorStatus';
export type { StepStatus } from '../../types/ui/stepStatus';
export { ConnectorStatus } from '../../types/ui/connectorStatus';
export type { ConnectorStatus as ConnectorStatusType } from '../../types/ui/connectorStatus';

export interface VerticalStepperStep {
  text: string;
  label?: string;
  status: StepStatus;
  /** Controls the visual style of the connector line drawn after this step. */
  connectorStatus?: ConnectorStatus;
}

interface VerticalStepperProps {
  steps: VerticalStepperStep[];
  /** Color used for DONE dot fills, NEXT dot outline, and filled connectors. */
  stepColor: string;
  /** Color used for DONE dot fills and DONE connector lines. */
  stepConnectorColor: string;
}

export function VerticalStepper({ steps, stepColor, stepConnectorColor }: VerticalStepperProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const { status } = step;

        const isDone = status === StepStatus.DONE;
        const isSkipped = status === StepStatus.SKIPPED;
        const isCurrent = status === StepStatus.CURRENT;

        const ringColor = stepColor;
        const cs = step.connectorStatus ?? ConnectorStatus.NOT_DONE;
        const connectorColor = cs === ConnectorStatus.DONE ? stepConnectorColor : '#F2F2F2';
        const shouldBlinkConnector = cs === ConnectorStatus.ACTIVE;

        const labelColor = isDone || isSkipped ? 'var(--text-secondary)' : 'var(--text-primary)';
        const labelExtraStyle: React.CSSProperties = isSkipped ? { textDecoration: 'line-through' } : {};
        const sublabelExtraStyle: React.CSSProperties = isSkipped ? { textDecoration: 'line-through' } : {};

        const outerStyle: React.CSSProperties = {
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          border: `2px solid ${ringColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxSizing: 'border-box',
          backgroundColor: 'white',
          position: 'relative',
          zIndex: 1
        };

        const innerStyle: React.CSSProperties = {
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isDone || isCurrent ? stepColor : 'transparent'
        };

        return (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Left column: radio box + connector strip */}
            <div
              style={{
                width: '20px',
                flexShrink: 0,
                position: 'relative',
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {!isLast && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    bottom: '-24px',
                    width: '8px',
                    backgroundColor: connectorColor,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    ...(shouldBlinkConnector
                      ? {
                          ['--vertical-stepper-connector-color' as string]: stepConnectorColor,
                          animation: 'verticalStepperConnectorPulse 1s ease-in-out infinite'
                        }
                      : {})
                  }}
                />
              )}
              <div style={outerStyle}>
                <div style={innerStyle} />
              </div>
            </div>

            {/* Content column */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              {step.label && (
                <Text as='span' variant='body2' style={{ color: labelColor, flexShrink: 0, ...sublabelExtraStyle }}>
                  {step.label}
                </Text>
              )}
              <Text
                as='span'
                variant='body2-medium'
                style={{
                  color: labelColor,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  ...labelExtraStyle
                }}
              >
                {step.text}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}
